import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  Alert,
  Link,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const Contact = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus('submitting');

    const accessKey = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY;

    if (!accessKey) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 5000);
      return;
    }

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          access_key: accessKey,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
        setTimeout(() => setSubmitStatus(null), 5000);
      } else {
        setSubmitStatus('error');
        setTimeout(() => setSubmitStatus(null), 5000);
      }
    } catch (error) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 5000);
    }
  };

  return (
    <Box
      id="contact"
      sx={{
        pt: { xs: 10, md: 12 },
        pb: { xs: 8, md: 12 },
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 3, sm: 4, md: 6 } }}>
        <Typography
          variant="h2"
          component="h2"
          sx={{
            textAlign: 'center',
            mb: { xs: 4, md: 6 },
            color: theme.palette.text.primary,
            fontWeight: 700,
            fontSize: { xs: '2rem', sm: '2.25rem', md: '2.5rem' },
          }}
        >
          Contact Us
        </Typography>
        <Grid container spacing={{ xs: 4, md: 6 }}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                height: '100%',
                border: `2px solid ${theme.palette.primary.main}`,
                borderRadius: 0,
              }}
            >
              <Typography
                variant="h4"
                component="h3"
                sx={{
                  mb: { xs: 3, md: 4 },
                  color: theme.palette.text.primary,
                  fontWeight: 600,
                  fontSize: { xs: '1.5rem', md: '2rem' },
                }}
              >
                Get in Touch
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PhoneIcon
                    sx={{
                      color: theme.palette.tertiary.main,
                      mr: 2,
                      fontSize: { xs: '1.25rem', md: '1.5rem' },
                    }}
                  />
                  <Link
                    href="tel:+15743498969"
                    underline="hover"
                    sx={{
                      fontSize: { xs: '1rem', md: '1.1rem' },
                      color: theme.palette.text.primary,
                      fontWeight: 500,
                    }}
                  >
                    (574) 349-8969
                  </Link>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <EmailIcon
                    sx={{
                      color: theme.palette.tertiary.main,
                      mr: 2,
                      fontSize: { xs: '1.25rem', md: '1.5rem' },
                    }}
                  />
                  <Link
                    href="mailto:info@jocastmachining.com"
                    underline="hover"
                    sx={{
                      fontSize: { xs: '0.9rem', md: '1.1rem' },
                      wordBreak: 'break-word',
                      color: theme.palette.text.primary,
                      fontWeight: 500,
                    }}
                  >
                    info@jocastmachining.com
                  </Link>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <LocationOnIcon
                    sx={{
                      color: theme.palette.tertiary.main,
                      mr: 2,
                      mt: 0.5,
                      fontSize: { xs: '1.25rem', md: '1.5rem' },
                    }}
                  />
                  <Link
                    href="https://www.google.com/maps/search/?api=1&query=24690+County+Road+38+Goshen+IN+46526"
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{
                      fontSize: { xs: '1rem', md: '1.1rem' },
                      color: theme.palette.text.primary,
                      fontWeight: 500,
                      display: 'inline-block',
                    }}
                  >
                    24690 County Road 38
                    <br />
                    Goshen, IN 46526
                  </Link>
                </Box>
              </Box>
              <Box
                sx={{
                  mt: 4,
                  width: '100%',
                  height: { xs: '250px', md: '300px' },
                  border: `1px solid ${theme.palette.primary.main}`,
                  overflow: 'hidden',
                  borderRadius: 0,
                }}
              >
                <Box
                  component="iframe"
                  title="JOCAST Machining Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3051.6056071347165!2d-85.80654752387301!3d41.58660687924409!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8816d0f5f5d6f8c3%3A0x0000000000000000!2s24690%20County%20Road%2038%2C%20Goshen%2C%20IN%2046526!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                  sx={{
                    border: 0,
                    width: '100%',
                    height: '100%',
                  }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                border: `2px solid ${theme.palette.primary.main}`,
                borderRadius: 0,
              }}
            >
              <Typography
                variant="h4"
                component="h3"
                sx={{
                  mb: { xs: 3, md: 4 },
                  color: theme.palette.text.primary,
                  fontWeight: 600,
                  fontSize: { xs: '1.5rem', md: '2rem' },
                }}
              >
                Send Us a Message
              </Typography>
              {submitStatus === 'success' && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  Thank you for your message! We'll get back to you soon.
                </Alert>
              )}
              {submitStatus === 'error' && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  There was an error sending your message. Please try again or contact us directly.
                </Alert>
              )}
              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  margin="normal"
                  sx={{ mb: 2 }}
                  size="medium"
                />
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  margin="normal"
                  sx={{ mb: 2 }}
                  size="medium"
                />
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  margin="normal"
                  sx={{ mb: 2 }}
                  size="medium"
                />
                <TextField
                  fullWidth
                  label="Message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  multiline
                  rows={6}
                  margin="normal"
                  sx={{ mb: 3 }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={submitStatus === 'submitting'}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.secondary.main,
                    py: { xs: 1.25, md: 1.5 },
                    fontSize: { xs: '0.95rem', md: '1rem' },
                    '&:hover': {
                      backgroundColor: theme.palette.tertiary.main,
                    },
                  }}
                >
                  {submitStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Contact;
